import Breadcrumb from "components/Common/Breadcrumb"
import TableContainer from "components/Common/TableContainer"
import { Formik } from "formik"
import {
    getAllAnnouncementAlerts,
    createAnnouncementAlert,
    updateAnnouncementAlert,
    uploadAnnouncementBanner,
} from "helpers/backendHelpers/announcementAlert"
import { SimpleStringValue } from "helpers/common_helper_functions"
import { SaveToast } from "components/Common/SaveToast"
import React, { useEffect, useMemo, useState, useCallback } from "react"
import Flatpickr from "react-flatpickr"
import classnames from "classnames"
import {
    Container,
    Modal,
    ModalHeader,
    ModalBody,
    Col,
    Row,
    Form,
    Input,
    Label,
    Card,
    CardBody,
    Button,
    Badge,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
} from "reactstrap"

const ALERT_TYPE_OPTIONS = [
    { value: "info", label: "Info" },
    { value: "warning", label: "Warning" },
    { value: "alert", label: "Alert" },
]

const ACTION_TYPE_OPTIONS = [
    { value: "custom", label: "Custom URL" },
    { value: "claimWhatsappTrial", label: "Claim WhatsApp Trial" },
]

const LANGUAGE_FIELDS = [
    { code: "en", label: "English", required: true },
    { code: "hi", label: "Hindi", required: false },
    { code: "gu", label: "Gujarati", required: false },
]

// Module-level so the object stays referentially stable across Formik re-renders;
// a fresh options object makes react-flatpickr reset the visible month on update.
// minDate hides past months/years; everything from today onwards stays selectable.
const DUE_DATE_PICKER_OPTIONS = {
    enableTime: true,
    allowInput: true,
    minuteIncrement: 1,
    minDate: "today",
    altInput: true,
    altFormat: "d M, Y h:i K",
    dateFormat: "Z",
}


const TYPE_BADGE_COLOR = {
    info: "primary",
    warning: "warning",
    alert: "danger",
}

// Dashboard banners are displayed at this exact ratio, so uploads must match the size.
const BANNER_WIDTH = 2042
const BANNER_HEIGHT = 381
// Allow a couple of pixels of difference from export rounding.
const BANNER_SIZE_TOLERANCE = 5

const readImageSize = file => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
        URL.revokeObjectURL(objectUrl)
        resolve({ width: image.naturalWidth, height: image.naturalHeight })
    }
    image.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        reject(new Error("Unable to read the selected image."))
    }
    image.src = objectUrl
})

const MAX_BANNER_SIZE = 5 * 1024 * 1024

const formatDate = dateStr => {
    if (!dateStr) return "—"
    try {
        const d = new Date(dateStr)
        return d.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        })
    } catch {
        return "—"
    }
}

const toLocalDatetimeValue = dateStr => {
    if (!dateStr) return ""
    try {
        const d = new Date(dateStr)
        const pad = n => String(n).padStart(2, "0")
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
            d.getDate()
        )}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    } catch {
        return ""
    }
}

const AnnouncementAlert = () => {
    const [allAlerts, setAllAlerts] = useState([])
    const [loading, setLoading] = useState(false)
    const [save, setSave] = useState(false)
    const [modal, setModal] = useState(false)
    const [form, setForm] = useState({})
    const [isEdit, setIsEdit] = useState(false)
    const [activeLangTab, setActiveLangTab] = useState("en")
    const [activeContentTab, setActiveContentTab] = useState("text")
    const [uploadingBanner, setUploadingBanner] = useState(false)

    useEffect(() => {
        fetchAllAlerts()
    }, [save])

    const fetchAllAlerts = useCallback(async () => {
        try {
            setLoading(true)
            const response = await getAllAnnouncementAlerts()
            setAllAlerts(response?.payload?.data || [])
        } catch (error) {
            // silently handle
        } finally {
            setLoading(false)
        }
    }, [])

    const handleAddAlert = async data => {
        try {
            await createAnnouncementAlert(data)
            setSave(prev => !prev)
            toggle()
            return true
        } catch (error) { }
    }

    const handleEditAlert = async (id, data) => {
        try {
            await updateAnnouncementAlert(id, data)
            setSave(prev => !prev)
            toggle()
            return true
        } catch (error) { }
    }

    const addButtonClick = () => {
        setIsEdit(false)
        setForm({ announcementType: "text", isActive: "true", actionType: "custom", url: "" })
        setActiveContentTab("text")
        toggle()
    }

    const editButtonClick = item => {
        setIsEdit(true)
        setForm({
            _id: item._id,
            announcementType: item.announcementType || "text",
            titleEn: item.title?.en || "",
            titleHi: item.title?.hi || "",
            titleGu: item.title?.gu || "",
            descriptionEn: item.description?.en || "",
            descriptionHi: item.description?.hi || "",
            descriptionGu: item.description?.gu || "",
            buttonLabelEn: item.buttonLabel?.en || "",
            buttonLabelHi: item.buttonLabel?.hi || "",
            buttonLabelGu: item.buttonLabel?.gu || "",
            type: item.type || "info",
            dueDate: item.dueDate ? new Date(item.dueDate).toISOString() : "",
            isActive: item.isActive !== undefined ? String(item.isActive) : "true",
            actionType: item.actionType || "custom",
            url: item.url || "",
            bannerUrl: item.bannerUrl || "",
            bannerName: item.bannerName || "",
        })
        setActiveContentTab(item.announcementType || "text")
        toggle()
    }

    const toggle = () => {
        if (modal) {
            setModal(false)
            setForm({})
            setIsEdit(false)
        } else {
            setModal(true)
        }
        setActiveLangTab("en")
    }

    const handleBannerUpload = async (event, setFieldValue) => {
        const file = event.target.files?.[0]
        event.target.value = ""
        if (!file) return

        if (!["image/jpeg", "image/png"].includes(file.type)) {
            SaveToast({ message: "Only JPG, JPEG, and PNG images are allowed.", type: "error" })
            return
        }
        if (file.size > MAX_BANNER_SIZE) {
            SaveToast({ message: "Banner image must be 5 MB or smaller.", type: "error" })
            return
        }
        try {
            const { width, height } = await readImageSize(file)
            if (Math.abs(width - BANNER_WIDTH) > BANNER_SIZE_TOLERANCE || Math.abs(height - BANNER_HEIGHT) > BANNER_SIZE_TOLERANCE) {
                SaveToast({ message: `Banner image must be ${BANNER_WIDTH} x ${BANNER_HEIGHT} px, ±${BANNER_SIZE_TOLERANCE} px (selected: ${width} x ${height} px).`, type: "error" })
                return
            }
        } catch (error) {
            SaveToast({ message: error.message, type: "error" })
            return
        }

        try {
            setUploadingBanner(true)
            const uploaded = await uploadAnnouncementBanner(file)
            setFieldValue("bannerUrl", uploaded?.url || "")
            SaveToast({ message: "Banner uploaded successfully.", type: "success" })
        } catch (error) {
            SaveToast({ message: error?.response?.data?.message || "Unable to upload banner image.", type: "error" })
        } finally {
            setUploadingBanner(false)
        }
    }

    const columns = useMemo(
        () => [
            {
                Header: "Title",
                id: "title",
                accessor: row => row.announcementType === "banner" ? (row.bannerName || "Banner") : (row.title?.en || ""),
                filterable: true,
                width: "15%",
                Cell: cellProps => <SimpleStringValue {...cellProps} />,
            },
            {
                Header: "Description",
                id: "description",
                accessor: row => row.announcementType === "banner" ? "Image banner" : (row.description?.en || ""),
                filterable: true,
                width: "35%",
                Cell: cellProps => <SimpleStringValue {...cellProps} />,
            },
            {
                Header: "Type",
                accessor: row => row.announcementType === "banner" ? "banner" : row.type,
                filterable: true,
                width: "10%",
                Cell: ({ value }) => (
                    <Badge color={value === "banner" ? "info" : (TYPE_BADGE_COLOR[value] || "secondary")}>
                        {value ? value.charAt(0).toUpperCase() + value.slice(1) : "—"}
                    </Badge>
                ),
            },
            {
                Header: "Due Date",
                accessor: "dueDate",
                filterable: true,
                width: "15%",
                Cell: ({ value }) => formatDate(value),
            },
            {
                Header: "Status",
                id: "status",
                accessor: row => {
                    if (!row.isActive) return "Inactive"
                    if (row.dueDate && new Date(row.dueDate) < new Date()) {
                        return "Expired"
                    }
                    return "Active"
                },
                disableFilters: false,
                width: "15%",
                Cell: ({ value }) => {
                    if (value === "Inactive") {
                        return <Badge color="danger">Inactive</Badge>
                    }
                    if (value === "Expired") {
                        return <Badge color="secondary">Expired</Badge>
                    }
                    return <Badge color="success">Active</Badge>
                },
            },
            {
                Header: "Actions",
                accessor: "actions",
                disableFilters: true,
                width: "10%",
                Cell: ({ row }) => {
                    const item = row.original
                    return (
                        <div className="d-flex align-items-center justify-content-center gap-3">
                            <Button
                                color="primary"
                                outline
                                size="sm"
                                onClick={() => editButtonClick(item)}
                            >
                                <i className="bx bx-edit-alt" />
                            </Button>
                        </div>
                    )
                },
            },
        ],
        []
    )

    document.title = "Dashboard Announcement Alerts | BillSaathi Admin"

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumb title="Dashboard Announcement Alerts" />

                    <Row>
                        <Col xs="12">
                            <Card>
                                <CardBody>
                                    <TableContainer
                                        columns={columns}
                                        data={allAlerts}
                                        isGlobalFilter={true}
                                        isAddOptions={true}
                                        addButtonLabel={"Add Alert"}
                                        handleAddButtonClick={addButtonClick}
                                        customPageSize={10}
                                        className="custom-header-css"
                                        dataFetchLoading={loading}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Create / Edit Modal */}
                    <Modal isOpen={modal} toggle={toggle} size="lg">
                        <ModalHeader toggle={toggle}>
                            {isEdit ? "Edit Alert" : "Add Alert"}
                        </ModalHeader>
                        <ModalBody>
                            <Formik
                                enableReinitialize={true}
                                initialValues={form}
                                onSubmit={async (values, { resetForm }) => {
                                    const announcementType = values.announcementType || "text"
                                    if (announcementType === "banner" && !values.bannerName?.trim()) {
                                        SaveToast({ message: "Please enter the banner name.", type: "error" })
                                        return
                                    }
                                    if (announcementType === "banner" && !values.bannerUrl) {
                                        SaveToast({ message: "Please upload a banner image before saving.", type: "error" })
                                        return
                                    }
                                    if (announcementType === "banner" && !values.dueDate) {
                                        SaveToast({ message: "Please select the banner duration end date and time.", type: "error" })
                                        return
                                    }
                                    const actionType = values.actionType || "custom"
                                    const payload = announcementType === "banner" ? {
                                        announcementType,
                                        bannerName: values.bannerName.trim(),
                                        bannerUrl: values.bannerUrl,
                                        dueDate: values.dueDate,
                                        isActive: values.isActive === "true",
                                    } : {
                                        announcementType,
                                        title: {
                                            en: values.titleEn || "",
                                            hi: values.titleHi || "",
                                            gu: values.titleGu || "",
                                        },
                                        body: values.titleEn,
                                        description: {
                                            en: values.descriptionEn || "",
                                            hi: values.descriptionHi || "",
                                            gu: values.descriptionGu || "",
                                        },
                                        type: values.type,
                                        dueDate: values.dueDate || undefined,
                                        isActive: values.isActive === "true",
                                        actionType,
                                        url: actionType === "custom" ? (values.url || "") : "",
                                        buttonLabel: {
                                            en: values.buttonLabelEn || "",
                                            hi: values.buttonLabelHi || "",
                                            gu: values.buttonLabelGu || "",
                                        },
                                    }
                                    let response
                                    if (isEdit && form._id) {
                                        response = await handleEditAlert(form._id, payload)
                                    } else {
                                        response = await handleAddAlert(payload)
                                    }
                                    if (response) {
                                        resetForm()
                                    }
                                }}
                            >
                                {({ values, handleSubmit, handleBlur, handleChange, setFieldValue }) => (
                                    <>
                                        <Form
                                            onSubmit={e => {
                                                e.preventDefault()
                                                handleSubmit(e)
                                                return false
                                            }}
                                        >
                                            <Row>
                                                <Col>
                                                    <Row className="mb-3">
                                                        <Col md={12}>
                                                            <Nav tabs>
                                                                <NavItem>
                                                                    <NavLink
                                                                        className={classnames({ active: activeContentTab === "text" })}
                                                                        onClick={() => {
                                                                            setActiveContentTab("text")
                                                                            setFieldValue("announcementType", "text")
                                                                        }}
                                                                        style={{ cursor: "pointer" }}
                                                                    >
                                                                        Text Based
                                                                    </NavLink>
                                                                </NavItem>
                                                                <NavItem>
                                                                    <NavLink
                                                                        className={classnames({ active: activeContentTab === "banner" })}
                                                                        onClick={() => {
                                                                            setActiveContentTab("banner")
                                                                            setFieldValue("announcementType", "banner")
                                                                        }}
                                                                        style={{ cursor: "pointer" }}
                                                                    >
                                                                        Banner Based
                                                                    </NavLink>
                                                                </NavItem>
                                                            </Nav>
                                                        </Col>
                                                    </Row>

                                                    {activeContentTab === "text" && <>
                                                    {/* Title + Description, one tab per language */}
                                                    <Row className="mb-3">
                                                        <Col md={12}>
                                                            <Nav tabs>
                                                                {LANGUAGE_FIELDS.map(lang => (
                                                                    <NavItem key={`tab-${lang.code}`}>
                                                                        <NavLink
                                                                            className={classnames({ active: activeLangTab === lang.code })}
                                                                            onClick={() => setActiveLangTab(lang.code)}
                                                                            style={{ cursor: "pointer" }}
                                                                        >
                                                                            {lang.label}
                                                                            {lang.required && <span className="text-danger"> *</span>}
                                                                        </NavLink>
                                                                    </NavItem>
                                                                ))}
                                                            </Nav>
                                                            <TabContent activeTab={activeLangTab} className="border border-top-0 p-3">
                                                                {LANGUAGE_FIELDS.map(lang => {
                                                                    const langKey = `${lang.code.charAt(0).toUpperCase()}${lang.code.slice(1)}`
                                                                    return (
                                                                        <TabPane tabId={lang.code} key={`pane-${lang.code}`}>
                                                                            <Row className="mb-3">
                                                                                <Col md={12}>
                                                                                    <Label className="form-label">
                                                                                        Title
                                                                                        {lang.required && <span className="text-danger"> *</span>}
                                                                                    </Label>
                                                                                    <Input
                                                                                        name={`title${langKey}`}
                                                                                        type="text"
                                                                                        placeholder={`Enter alert title in ${lang.label}${lang.required ? "" : " (optional)"}`}
                                                                                        onChange={handleChange}
                                                                                        onBlur={handleBlur}
                                                                                        value={values[`title${langKey}`] || ""}
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                            <Row>
                                                                                <Col md={12}>
                                                                                    <Label className="form-label">
                                                                                        Description
                                                                                        {lang.required && <span className="text-danger"> *</span>}
                                                                                    </Label>
                                                                                    <Input
                                                                                        name={`description${langKey}`}
                                                                                        type="textarea"
                                                                                        placeholder={`Enter description in ${lang.label}${lang.required ? "" : " (optional)"}...`}
                                                                                        style={{ width: "100%", height: "120px" }}
                                                                                        onChange={handleChange}
                                                                                        onBlur={handleBlur}
                                                                                        value={values[`description${langKey}`] || ""}
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                            <Row>
                                                                                <Col md={12}>
                                                                                    <Label className="form-label">Button Label</Label>
                                                                                    <Input
                                                                                        name={`buttonLabel${langKey}`}
                                                                                        type="text"
                                                                                        placeholder={`Enter button label in ${lang.label} (optional)`}
                                                                                        onChange={handleChange}
                                                                                        onBlur={handleBlur}
                                                                                        value={values[`buttonLabel${langKey}`] || ""}
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                        </TabPane>
                                                                    )
                                                                })}
                                                            </TabContent>
                                                        </Col>
                                                    </Row>

                                                    {/* Type */}
                                                    <Row className="mb-3">
                                                        <Col md={12}>
                                                            <Label className="form-label">Type</Label>
                                                            <Input
                                                                name="type"
                                                                type="select"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                value={values.type || ""}
                                                            >
                                                                <option value="">Select Type</option>
                                                                {ALERT_TYPE_OPTIONS.map(opt => (
                                                                    <option key={opt.value} value={opt.value}>
                                                                        {opt.label}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </Col>
                                                    </Row>

                                                    {/* Action Type */}
                                                    <Row className="mb-3">
                                                        <Col md={12}>
                                                            <Label className="form-label">Action Type</Label>
                                                            <Input
                                                                name="actionType"
                                                                type="select"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                value={values.actionType || "custom"}
                                                            >
                                                                {ACTION_TYPE_OPTIONS.map(opt => (
                                                                    <option key={opt.value} value={opt.value}>
                                                                        {opt.label}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </Col>
                                                    </Row>

                                                    {/* URL */}
                                                    {(values.actionType || "custom") === "custom" && (
                                                        <Row className="mb-3">
                                                            <Col md={12}>
                                                                <Label className="form-label">URL</Label>
                                                                <Input
                                                                    name="url"
                                                                    type="text"
                                                                    placeholder="Enter button URL (optional)"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={values.url || ""}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    )}
                                                    </>}

                                                    {activeContentTab === "banner" && <>
                                                        <Row className="mb-3">
                                                            <Col md={12}>
                                                                <Label className="form-label">Banner Name <span className="text-danger">*</span></Label>
                                                                <Input
                                                                    name="bannerName"
                                                                    placeholder="Enter banner name"
                                                                    value={values.bannerName || ""}
                                                                    onChange={handleChange}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row className="mb-3">
                                                            <Col md={12}>
                                                                <Label className="form-label">Banner Image <span className="text-danger">*</span></Label>
                                                                <Input
                                                                    type="file"
                                                                    accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                                                                    onChange={event => handleBannerUpload(event, setFieldValue)}
                                                                    disabled={uploadingBanner}
                                                                />
                                                                <small className="text-muted d-block mt-1">
                                                                    JPG, JPEG, or PNG only; 2042 x 381 px (±2 px); maximum 5 MB.
                                                                </small>
                                                                {values.bannerUrl && (
                                                                    <img
                                                                        src={values.bannerUrl}
                                                                        alt="Banner preview"
                                                                        className="img-fluid border rounded mt-2"
                                                                        style={{ width: "100%" }}
                                                                    />
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    </>}

                                                    {/* Duration */}
                                                    <Row className="mb-3">
                                                        <Col md={12}>
                                                            <Label className="form-label">
                                                                {activeContentTab === "banner" ? "Banner Duration End Date" : "Due Date"}
                                                            </Label>
                                                            <Flatpickr
                                                                className="form-control d-block"
                                                                placeholder="Select Due Date & Time..."
                                                                name="dueDate"
                                                                options={DUE_DATE_PICKER_OPTIONS}
                                                                value={values.dueDate || null}
                                                                onChange={date => {
                                                                    setFieldValue("dueDate", date?.length ? date[0].toISOString() : "")
                                                                }}
                                                            />
                                                        </Col>
                                                    </Row>

                                                    {/* Status */}
                                                    {isEdit && (
                                                        <Row className="mb-3">
                                                            <Col md={12}>
                                                                <Label className="form-label">Status</Label>
                                                                <Input
                                                                    name="isActive"
                                                                    type="select"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={values.isActive || "true"}
                                                                >
                                                                    <option value="true">Active</option>
                                                                    <option value="false">Inactive</option>
                                                                </Input>
                                                            </Col>
                                                        </Row>
                                                    )}

                                                    {/* Buttons */}
                                                    <Row className="mb-3 text-center">
                                                        <Col>
                                                            <div className="text-center">
                                                                <Button
                                                                    size="md"
                                                                    color="danger"
                                                                    type="button"
                                                                    className="mx-2"
                                                                    onClick={toggle}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <Button
                                                                    type="submit"
                                                                    className="btn btn-success save-user"
                                                                    disabled={uploadingBanner}
                                                                >
                                                                    {isEdit ? "Update" : "Save"}
                                                                </Button>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </Form>
                                    </>
                                )}
                            </Formik>
                        </ModalBody>
                    </Modal>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default AnnouncementAlert
